import * as Y from "yjs";
import { clearDomainMaps, type DomainMaps, LOCAL_ORIGIN } from "@electro-planner/domain";

/**
 * Backup-format: en binär `.ep`-fil som innehåller hela Y.Doc-state som en
 * Yjs-update. Kan laddas tillbaka via restoreDocFromBackup som rensar doc:ens
 * alla maps och applicerar update:n — allt i ett atomärt transact() så undo/redo
 * bevarar möjligheten att ångra en felaktig import.
 *
 * Filformatet är framåtkompatibelt så länge Yjs-protokollet är.
 */
const BACKUP_MIME = "application/octet-stream";
const BACKUP_EXTENSION = "ep";

export function encodeDocToBackup(doc: Y.Doc): Uint8Array {
  return Y.encodeStateAsUpdate(doc);
}

/**
 * Applicerar en backup-update på doc:en och rensar samtidigt all existerande
 * domändata. Måste göras i samma transaction så det är ETT undo-steg.
 * Origin = LOCAL_ORIGIN så UndoManager fångar det som en användarhandling.
 */
export function restoreDocFromBackup(
  doc: Y.Doc,
  maps: DomainMaps,
  bytes: Uint8Array,
): void {
  doc.transact(() => {
    clearDomainMaps(maps);
    Y.applyUpdate(doc, bytes, LOCAL_ORIGIN);
  }, LOCAL_ORIGIN);
}

export function downloadBackup(
  doc: Y.Doc,
  documentId: string,
  date: Date = new Date(),
): void {
  const update = encodeDocToBackup(doc);
  // Web API tar ArrayBuffer-likt; en Uint8Array-view är kompatibel med BlobPart.
  const blob = new Blob([update as BlobPart], { type: BACKUP_MIME });
  const filename = buildBackupFilename(documentId, date);
  triggerDownload(blob, filename);
}

export function buildBackupFilename(documentId: string, date: Date): string {
  const iso = date.toISOString().slice(0, 10);
  const safeId = documentId.replace(/[^a-zA-Z0-9_-]/g, "_");
  return `electro-planner-${safeId}-${iso}.${BACKUP_EXTENSION}`;
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Liten fördröjning så browsern hinner starta nedladdningen innan URL:n rivs.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
